import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, Users, Search, Phone, Mail, Building2, TrendingUp, CheckCircle, XCircle, Clock, BarChart3, LayoutGrid, Upload, Download, FileText, Loader2, Send } from 'lucide-react';
import { leadService } from '../../services/leadService';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

// Mapeamento de status para exibição
const statusLabels = {
  'novo': 'Novo',
  'em_contato': 'Contato Feito',
  'qualificado': 'Qualificado',
  'proposta_enviada': 'Proposta Enviada',
  'negociacao': 'Negociação',
  'ganho': 'Ganho',
  'perdido': 'Perdido'
};

const statusColors = {
  'novo': 'bg-blue-100 text-blue-800',
  'em_contato': 'bg-purple-100 text-purple-800',
  'qualificado': 'bg-cyan-100 text-cyan-800',
  'proposta_enviada': 'bg-yellow-100 text-yellow-800',
  'negociacao': 'bg-orange-100 text-orange-800',
  'ganho': 'bg-green-100 text-green-800',
  'perdido': 'bg-red-100 text-red-800'
};

// Função para formatar telefone
const formatPhone = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 11) {
    if (numbers.length <= 10) {
      return numbers
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return numbers
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  }
  return value;
};

const Leads = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [interactionDialogOpen, setInteractionDialogOpen] = useState(false);
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({
    total_leads: 0,
    status_breakdown: {},
    conversion_rate: 0,
    total_proposal_value: 0
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
    next_followup_date: ''
  });

  const [interactionData, setInteractionData] = useState({
    interaction_type: 'call',
    summary: '',
    next_steps: '',
    duration_minutes: ''
  });

  const [proposalData, setProposalData] = useState({
    title: '',
    description: '',
    estimated_value: '',
    expiration_date: ''
  });

  // Estados para upload de arquivos
  const [uploadingFile, setUploadingFile] = useState(null); // ID da proposta sendo uploaded
  const [downloadingFile, setDownloadingFile] = useState(null);

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [filterStatus]);

  // Verificar se deve abrir modal de detalhes automaticamente
  useEffect(() => {
    const leadIdToOpen = sessionStorage.getItem('openLeadDetail');
    if (leadIdToOpen && leads.length > 0) {
      const lead = leads.find(l => l._id === leadIdToOpen);
      if (lead) {
        handleViewDetails(lead);
        sessionStorage.removeItem('openLeadDetail');
      }
    }
  }, [leads]);

  const fetchData = async () => {
    try {
      const status = filterStatus !== 'all' ? filterStatus : null;
      const leadsData = await leadService.getAll(status);
      setLeads(leadsData || []);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      setLeads([]);
      toast({
        title: 'Erro ao carregar leads',
        description: 'Não foi possível carregar os leads.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await leadService.getDashboardStats();
      setStats(statsData || {
        total_leads: 0,
        status_breakdown: {},
        conversion_rate: 0,
        total_proposal_value: 0
      });
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      setStats({
        total_leads: 0,
        status_breakdown: {},
        conversion_rate: 0,
        total_proposal_value: 0
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingLead) {
        await leadService.update(editingLead._id, formData);
        toast({
          title: 'Lead atualizado',
          description: 'Lead atualizado com sucesso!',
        });
      } else {
        await leadService.create(formData);
        toast({
          title: 'Lead criado',
          description: 'Lead criado com sucesso!',
        });
      }
      
      setDialogOpen(false);
      setEditingLead(null);
      resetForm();
      fetchData();
      fetchStats();
    } catch (error) {
      console.error('Erro ao salvar lead:', error);
      toast({
        title: 'Erro ao salvar lead',
        description: error.response?.data?.detail || 'Não foi possível salvar o lead.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      company: lead.company,
      notes: lead.notes || '',
      next_followup_date: lead.next_followup_date ? new Date(lead.next_followup_date).toISOString().slice(0, 16) : ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (leadId) => {
    if (window.confirm('Tem certeza que deseja excluir este lead?')) {
      try {
        await leadService.delete(leadId);
        toast({
          title: 'Lead excluído',
          description: 'Lead excluído com sucesso!',
        });
        fetchData();
        fetchStats();
      } catch (error) {
        console.error('Erro ao excluir lead:', error);
        toast({
          title: 'Erro ao excluir lead',
          description: 'Não foi possível excluir o lead.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleViewDetails = async (lead) => {
    setSelectedLead(lead);
    setDetailDialogOpen(true);
    
    try {
      const [interactionsData, proposalsData] = await Promise.all([
        leadService.getInteractions(lead._id),
        leadService.getProposals(lead._id)
      ]);
      setInteractions(interactionsData);
      setProposals(proposalsData);
    } catch (error) {
      console.error('Erro ao buscar detalhes:', error);
    }
  };

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    
    try {
      await leadService.createInteraction(selectedLead._id, {
        interaction_type: interactionData.interaction_type,
        summary: interactionData.summary,
        next_steps: interactionData.next_steps,
        duration_minutes: interactionData.duration_minutes ? parseInt(interactionData.duration_minutes) : null
      });
      
      toast({
        title: 'Interação registrada',
        description: 'Interação registrada com sucesso!',
      });
      
      setInteractionDialogOpen(false);
      resetInteractionForm();
      
      // Atualizar lista de interações
      const interactionsData = await leadService.getInteractions(selectedLead._id);
      setInteractions(interactionsData);
      fetchData();
    } catch (error) {
      console.error('Erro ao registrar interação:', error);
      toast({
        title: 'Erro ao registrar interação',
        description: error.response?.data?.detail || 'Não foi possível registrar a interação.',
        variant: 'destructive',
      });
    }
  };

  const handleAddProposal = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        title: proposalData.title,
        description: proposalData.description,
        estimated_value: parseFloat(proposalData.estimated_value)
      };
      
      // Adicionar data de validade se preenchida
      if (proposalData.expiration_date) {
        payload.expiration_date = new Date(proposalData.expiration_date).toISOString();
      }
      
      await leadService.createProposal(selectedLead._id, payload);
      
      toast({
        title: 'Proposta criada',
        description: 'Proposta criada com sucesso!',
      });
      
      setProposalDialogOpen(false);
      resetProposalForm();
      
      // Atualizar lista de propostas
      const proposalsData = await leadService.getProposals(selectedLead._id);
      setProposals(proposalsData);
    } catch (error) {
      console.error('Erro ao criar proposta:', error);
      toast({
        title: 'Erro ao criar proposta',
        description: error.response?.data?.detail || 'Não foi possível criar a proposta.',
        variant: 'destructive',
      });
    }
  };

  const handleConvertToClient = async (leadId) => {
    if (window.confirm('Tem certeza que deseja converter este lead em cliente?')) {
      try {
        await leadService.convertToClient(leadId);
        toast({
          title: 'Lead convertido',
          description: 'Lead convertido em cliente com sucesso!',
        });
        fetchData();
        fetchStats();
        setDetailDialogOpen(false);
      } catch (error) {
        console.error('Erro ao converter lead:', error);
        toast({
          title: 'Erro ao converter lead',
          description: error.response?.data?.detail || 'Não foi possível converter o lead.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      await leadService.update(leadId, { status: newStatus });
      toast({
        title: 'Status atualizado',
        description: 'Status do lead atualizado com sucesso!',
      });
      fetchData();
      fetchStats();
      
      // Atualizar selectedLead se estiver aberto
      if (selectedLead && selectedLead._id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast({
        title: 'Erro ao atualizar status',
        description: 'Não foi possível atualizar o status.',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateProposalStatus = async (leadId, proposalId, newStatus) => {
    try {
      await leadService.updateProposal(leadId, proposalId, { status: newStatus });
      
      toast({
        title: 'Status atualizado',
        description: 'Status da proposta atualizado com sucesso!',
      });
      
      // Atualizar lista de propostas
      const proposalsData = await leadService.getProposals(selectedLead._id);
      setProposals(proposalsData);
      
      // Atualizar stats se a proposta mudou para aceita/rejeitada
      fetchStats();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast({
        title: 'Erro ao atualizar status',
        description: 'Não foi possível atualizar o status da proposta.',
        variant: 'destructive',
      });
    }
  };

  // ========== FUNÇÕES DE UPLOAD DE ARQUIVO ==========
  const handleFileSelect = async (event, proposalId) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar arquivo
    const validation = leadService.validateFile(file);
    if (!validation.valid) {
      toast({
        title: 'Arquivo inválido',
        description: validation.error,
        variant: 'destructive',
      });
      event.target.value = ''; // Limpar input
      return;
    }

    // Fazer upload imediatamente
    setUploadingFile(proposalId);
    try {
      await leadService.uploadProposalFile(proposalId, file);
      
      toast({
        title: 'Sucesso!',
        description: 'Arquivo anexado com sucesso.',
      });
      
      // Recarregar propostas
      const proposalsData = await leadService.getProposals(selectedLead._id);
      setProposals(proposalsData);
    } catch (error) {
      toast({
        title: 'Erro ao anexar arquivo',
        description: error.response?.data?.detail || 'Não foi possível anexar o arquivo.',
        variant: 'destructive',
      });
    } finally {
      setUploadingFile(null);
      event.target.value = ''; // Limpar input para permitir reenvio
    }
  };

  const handleDownloadFile = async (proposalId, fileName) => {
    setDownloadingFile(proposalId);
    try {
      await leadService.downloadProposalFile(proposalId, fileName);
      
      toast({
        title: 'Sucesso!',
        description: 'Arquivo baixado com sucesso.',
      });
    } catch (error) {
      toast({
        title: 'Erro ao baixar arquivo',
        description: 'Não foi possível baixar o arquivo.',
        variant: 'destructive',
      });
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleDeleteFile = async (proposalId) => {
    if (!window.confirm('Tem certeza que deseja remover o arquivo anexado?')) return;

    try {
      await leadService.deleteProposalFile(proposalId);
      
      toast({
        title: 'Sucesso!',
        description: 'Arquivo removido com sucesso.',
      });

      // Recarregar propostas
      const proposalsData = await leadService.getProposals(selectedLead._id);
      setProposals(proposalsData);
    } catch (error) {
      toast({
        title: 'Erro ao remover arquivo',
        description: 'Não foi possível remover o arquivo.',
        variant: 'destructive',
      });
    }
  };


  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      notes: '',
      next_followup_date: ''
    });
  };

  const resetInteractionForm = () => {
    setInteractionData({
      interaction_type: 'call',
      summary: '',
      next_steps: '',
      duration_minutes: ''
    });
  };

  const resetProposalForm = () => {
    setProposalData({
      title: '',
      description: '',
      estimated_value: '',
      expiration_date: ''
    });
  };

  const filteredLeads = (leads || []).filter(lead =>
    lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header com Estatísticas */}
      <div>
        <h1 className="text-3xl font-bold mb-6">CRM - Gestão de Leads</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total de Leads</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_leads || 0}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Taxa de Conversão</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats?.conversion_rate || 0}%</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Leads Ganhos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats?.status_breakdown?.ganho || 0}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Valor Total de Propostas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                R$ {(stats?.total_proposal_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Barra de ações */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle>Leads</CardTitle>
              <CardDescription>Gerencie seus leads e oportunidades</CardDescription>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/portal/crm/dashboard')}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                Dashboard
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/portal/crm/kanban')}
              >
                <LayoutGrid className="mr-2 h-4 w-4" />
                Kanban
              </Button>
              
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => { setEditingLead(null); resetForm(); }}>
                    <Plus className="mr-2 h-4 w-4" />
                    Novo Lead
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingLead ? 'Editar Lead' : 'Novo Lead'}</DialogTitle>
                  <DialogDescription>
                    {editingLead ? 'Atualize as informações do lead' : 'Preencha os dados do novo lead'}
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nome do Contato *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="company">Empresa *</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">E-mail *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone">Telefone *</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="notes">Observações</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="next_followup_date">Próximo Follow-up (Data de Retorno)</Label>
                    <Input
                      id="next_followup_date"
                      type="datetime-local"
                      value={formData.next_followup_date}
                      onChange={(e) => setFormData({ ...formData, next_followup_date: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Define quando este lead deve ser contatado novamente
                    </p>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit">
                      {editingLead ? 'Atualizar' : 'Criar Lead'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        </CardHeader>
        
        <CardContent>
          {/* Filtros e Busca */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, e-mail ou empresa..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                <SelectItem value="novo">Novo</SelectItem>
                <SelectItem value="em_contato">Contato Feito</SelectItem>
                <SelectItem value="qualificado">Qualificado</SelectItem>
                <SelectItem value="proposta_enviada">Proposta Enviada</SelectItem>
                <SelectItem value="negociacao">Negociação</SelectItem>
                <SelectItem value="ganho">Ganho</SelectItem>
                <SelectItem value="perdido">Perdido</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Lista de Leads */}
          {filteredLeads.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhum lead encontrado</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredLeads.map((lead) => (
                <Card key={lead._id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{lead.name}</CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <Building2 className="h-3 w-3" />
                          {lead.company}
                        </CardDescription>
                      </div>
                      <Badge className={statusColors[lead.status]}>
                        {statusLabels[lead.status]}
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        {lead.email}
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        {lead.phone}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleViewDetails(lead)}
                      >
                        Ver Detalhes
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(lead)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(lead._id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Detalhes do Lead */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selectedLead.name}</DialogTitle>
                <DialogDescription>{selectedLead.company}</DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                {/* Informações Básicas */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">E-mail</Label>
                    <p className="font-medium">{selectedLead.email}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Telefone</Label>
                    <p className="font-medium">{selectedLead.phone}</p>
                  </div>
                </div>

                {/* Status e Ações */}
                <div>
                  <Label>Status</Label>
                  <div className="flex gap-2 mt-2">
                    <Select value={selectedLead.status} onValueChange={(value) => handleUpdateStatus(selectedLead._id, value)}>
                      <SelectTrigger className="w-[250px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="novo">Novo</SelectItem>
                        <SelectItem value="em_contato">Contato Feito</SelectItem>
                        <SelectItem value="qualificado">Qualificado</SelectItem>
                        <SelectItem value="proposta_enviada">Proposta Enviada</SelectItem>
                        <SelectItem value="negociacao">Negociação</SelectItem>
                        <SelectItem value="ganho">Ganho</SelectItem>
                        <SelectItem value="perdido">Perdido</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {selectedLead.status === 'ganho' && !selectedLead.converted_to_client_id && (
                      <Button onClick={() => handleConvertToClient(selectedLead._id)} variant="default">
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Converter em Cliente
                      </Button>
                    )}
                  </div>
                </div>

                {/* Tabs de Interações e Propostas */}
                <Tabs defaultValue="interactions" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="interactions">Interações ({interactions.length})</TabsTrigger>
                    <TabsTrigger value="proposals">Propostas ({proposals.length})</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="interactions" className="space-y-4">
                    <Dialog open={interactionDialogOpen} onOpenChange={setInteractionDialogOpen}>
                      <DialogTrigger asChild>
                        <Button onClick={resetInteractionForm}>
                          <Plus className="mr-2 h-4 w-4" />
                          Nova Interação
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Registrar Interação</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddInteraction} className="space-y-4">
                          <div>
                            <Label>Tipo</Label>
                            <Select
                              value={interactionData.interaction_type}
                              onValueChange={(value) => setInteractionData({ ...interactionData, interaction_type: value })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="call">Ligação</SelectItem>
                                <SelectItem value="email">E-mail</SelectItem>
                                <SelectItem value="meeting">Reunião</SelectItem>
                                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                                <SelectItem value="other">Outro</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label>Resumo *</Label>
                            <Textarea
                              value={interactionData.summary}
                              onChange={(e) => setInteractionData({ ...interactionData, summary: e.target.value })}
                              required
                              rows={3}
                            />
                          </div>
                          
                          <div>
                            <Label>Próximos Passos</Label>
                            <Textarea
                              value={interactionData.next_steps}
                              onChange={(e) => setInteractionData({ ...interactionData, next_steps: e.target.value })}
                              rows={2}
                            />
                          </div>
                          
                          <div>
                            <Label>Duração (minutos)</Label>
                            <Input
                              type="number"
                              value={interactionData.duration_minutes}
                              onChange={(e) => setInteractionData({ ...interactionData, duration_minutes: e.target.value })}
                            />
                          </div>
                          
                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setInteractionDialogOpen(false)}>
                              Cancelar
                            </Button>
                            <Button type="submit">Registrar</Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                    
                    <div className="space-y-3">
                      {interactions.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">Nenhuma interação registrada</p>
                      ) : (
                        interactions.map((interaction) => (
                          <Card key={interaction.id}>
                            <CardContent className="pt-4">
                              <div className="flex justify-between items-start mb-2">
                                <Badge variant="outline">{interaction.interaction_type}</Badge>
                                <span className="text-sm text-muted-foreground">
                                  {new Date(interaction.created_at).toLocaleDateString('pt-BR')}
                                </span>
                              </div>
                              <p className="text-sm mb-2">{interaction.summary}</p>
                              {interaction.next_steps && (
                                <p className="text-sm text-muted-foreground">
                                  <strong>Próximos passos:</strong> {interaction.next_steps}
                                </p>
                              )}
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="proposals" className="space-y-4">
                    <Dialog open={proposalDialogOpen} onOpenChange={setProposalDialogOpen}>
                      <DialogTrigger asChild>
                        <Button onClick={resetProposalForm}>
                          <Plus className="mr-2 h-4 w-4" />
                          Nova Proposta
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Criar Proposta</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddProposal} className="space-y-4">
                          <div>
                            <Label>Título *</Label>
                            <Input
                              value={proposalData.title}
                              onChange={(e) => setProposalData({ ...proposalData, title: e.target.value })}
                              required
                            />
                          </div>
                          
                          <div>
                            <Label>Descrição</Label>
                            <Textarea
                              value={proposalData.description}
                              onChange={(e) => setProposalData({ ...proposalData, description: e.target.value })}
                              rows={3}
                            />
                          </div>
                          
                          <div>
                            <Label>Valor Estimado *</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={proposalData.estimated_value}
                              onChange={(e) => setProposalData({ ...proposalData, estimated_value: e.target.value })}
                              required
                            />
                          </div>
                          
                          <div>
                            <Label>Data de Validade</Label>
                            <Input
                              type="date"
                              value={proposalData.expiration_date}
                              onChange={(e) => setProposalData({ ...proposalData, expiration_date: e.target.value })}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Data limite para aceitação da proposta. <span className="font-medium text-purple-600">Propostas com status "Enviada" ou "Em Análise" aparecerão nos alertas quando próximas do vencimento (últimos 3 dias).</span>
                            </p>
                          </div>
                          
                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setProposalDialogOpen(false)}>
                              Cancelar
                            </Button>
                            <Button type="submit">Criar Proposta</Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                    
                    <div className="space-y-3">
                      {proposals.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">Nenhuma proposta criada</p>
                      ) : (
                        proposals.map((proposal) => (
                          <Card key={proposal._id} className="border-2">
                            <CardContent className="pt-6">
                              <div className="space-y-4">
                                {/* Cabeçalho da Proposta */}
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-lg">{proposal.title}</h4>
                                    {proposal.description && (
                                      <p className="text-sm text-gray-600 mt-1">{proposal.description}</p>
                                    )}
                                  </div>
                                  <Select 
                                    value={proposal.status} 
                                    onValueChange={(value) => handleUpdateProposalStatus(selectedLead._id, proposal._id, value)}
                                  >
                                    <SelectTrigger className="w-[150px]">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="rascunho">Rascunho</SelectItem>
                                      <SelectItem value="enviada">Enviada</SelectItem>
                                      <SelectItem value="em_analise">Em Análise</SelectItem>
                                      <SelectItem value="aceita">Aceita</SelectItem>
                                      <SelectItem value="recusada">Recusada</SelectItem>
                                      <SelectItem value="expirada">Expirada</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Informações da Proposta */}
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <Label className="text-gray-600">Valor</Label>
                                    <p className="font-medium text-lg text-green-600">
                                      R$ {proposal.estimated_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-gray-600">Criada em</Label>
                                    <p className="font-medium">
                                      {new Date(proposal.created_at).toLocaleDateString('pt-BR')}
                                    </p>
                                  </div>
                                </div>

                                {/* SEÇÃO DE ARQUIVO */}
                                <div className="border-t pt-4">
                                  <Label className="text-gray-700 mb-2 block">Arquivo Anexado</Label>
                                  
                                  {proposal.file_name ? (
                                    <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                      <div className="flex items-center gap-3">
                                        <FileText className="h-5 w-5 text-blue-500" />
                                        <div>
                                          <p className="font-medium text-sm">{proposal.file_name}</p>
                                          <p className="text-xs text-gray-500">
                                            {leadService.formatFileSize(proposal.file_size)}
                                          </p>
                                        </div>
                                      </div>
                                      
                                      <div className="flex gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleDownloadFile(proposal._id, proposal.file_name)}
                                          disabled={downloadingFile === proposal._id}
                                          className="gap-2"
                                        >
                                          {downloadingFile === proposal._id ? (
                                            <>
                                              <Loader2 className="h-4 w-4 animate-spin" />
                                              Baixando...
                                            </>
                                          ) : (
                                            <>
                                              <Download className="h-4 w-4" />
                                              Download
                                            </>
                                          )}
                                        </Button>
                                        
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleDeleteFile(proposal._id)}
                                          className="text-red-600 hover:text-red-700"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                                      <div className="text-center">
                                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                        <p className="text-sm text-gray-600 mb-3">
                                          Nenhum arquivo anexado
                                        </p>
                                        
                                        <label htmlFor={`file-upload-${proposal._id}`}>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            asChild
                                            disabled={uploadingFile === proposal._id}
                                            className="gap-2"
                                          >
                                            <span className="cursor-pointer">
                                              {uploadingFile === proposal._id ? (
                                                <>
                                                  <Loader2 className="h-4 w-4 animate-spin" />
                                                  Enviando...
                                                </>
                                              ) : (
                                                <>
                                                  <Upload className="h-4 w-4" />
                                                  Anexar Arquivo
                                                </>
                                              )}
                                            </span>
                                          </Button>
                                        </label>
                                        <input
                                          id={`file-upload-${proposal._id}`}
                                          type="file"
                                          className="hidden"
                                          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
                                          onChange={(e) => handleFileSelect(e, proposal._id)}
                                        />
                                        
                                        <p className="text-xs text-gray-500 mt-2">
                                          PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, JPG (máx. 50 MB)
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
